// ═══════════════════════════════════════════════════════════════════════
//   DOCUMENTOS DNA SIGN — somente impressão (render_mode = print_only)
//   Consome os modelos e o resolvedor do DNA Sign. Não cria envelope,
//   não envia e-mail/WhatsApp e não altera status de assinatura.
// ═══════════════════════════════════════════════════════════════════════
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  FileText, Search, Loader2, Printer, Eye, Download, AlertTriangle, ArrowRight, X,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const API_URL = window.location.origin;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

// variável canônica → (rótulo humano, bloco do cadastro)
const VAR_MAP = {
  ALUNO_NOME: ['Nome do aluno', 'aluno'],
  ALUNO_MATRICULA: ['Número de matrícula', 'aluno'],
  ALUNO_NASCIMENTO: ['Data de nascimento', 'aluno'],
  ALUNO_CPF: ['CPF do aluno', 'documentos'],
  ALUNO_RG: ['RG do aluno', 'documentos'],
  ALUNO_RA: ['RA do aluno', 'documentos'],
  TURMA: ['Turma', 'aluno'],
  SERIE: ['Série', 'aluno'],
  ENDERECO: ['Endereço', 'contato'],
};
const varInfo = (v) => {
  if (VAR_MAP[v]) return VAR_MAP[v];
  if (v.startsWith('RESPONSAVEL')) return [`Responsável · ${v.replace(/^RESPONSAVEL_(DID_)?/, '').replaceAll('_', ' ').toLowerCase()}`, 'responsaveis'];
  if (v.startsWith('VALOR') || v.startsWith('PLANO') || v.startsWith('DESCONTO') || v.includes('PARCEL') || v.includes('VENCIMENTO')) {
    return [v.replaceAll('_', ' ').toLowerCase(), 'financeiro'];
  }
  if (v.startsWith('ALUNO')) return [v.replace('ALUNO_', 'Aluno · ').replaceAll('_', ' ').toLowerCase(), 'aluno'];
  return [v.replaceAll('_', ' ').toLowerCase(), 'aluno'];
};

const printHtml = (html) => {
  const frame = document.createElement('iframe');
  frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
  document.body.appendChild(frame);
  frame.onload = () => {
    try { frame.contentWindow.focus(); frame.contentWindow.print(); } catch { /* noop */ }
    setTimeout(() => frame.remove(), 60000);
  };
  frame.srcdoc = html;
};

const DocumentosDnaSignDialog = ({ open, onOpenChange, student, onGoToField }) => {
  const [loading, setLoading] = useState(false);
  const [catalog, setCatalog] = useState(null);
  const [busca, setBusca] = useState('');
  const [cat, setCat] = useState('todos');
  const [year, setYear] = useState('');
  const [busyId, setBusyId] = useState('');
  const [preview, setPreview] = useState(null); // {template, html, missing_required, ...}

  useEffect(() => {
    if (!open || !student?.id) return;
    setBusca(''); setCat('todos'); setPreview(null);
    setLoading(true);
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/dna-sign/print/catalog?student_id=${student.id}`, { headers: authHeaders() });
        const json = await res.json();
        if (!res.ok) throw new Error(typeof json.detail === 'string' ? json.detail : 'Não foi possível carregar os modelos');
        setCatalog(json);
        setYear(String(json.default_school_year || ''));
      } catch (e) { toast.error(e.message); } finally { setLoading(false); }
    })();
  }, [open, student?.id]);

  const items = useMemo(() => {
    const all = catalog?.items || [];
    const q = busca.trim().toLowerCase();
    return all.filter(t => (cat === 'todos' || t.category_key === cat)
      && (!q || `${t.name} ${t.category_label} ${t.document_type}`.toLowerCase().includes(q)));
  }, [catalog, busca, cat]);

  const qs = useCallback((tid) => {
    const p = new URLSearchParams({ template_id: tid, student_id: student.id });
    if (year) p.set('school_year', year);
    return p.toString();
  }, [student?.id, year]);

  const carregar = useCallback(async (t) => {
    const res = await fetch(`${API_URL}/api/dna-sign/print/render?${qs(t.id)}`, { headers: authHeaders() });
    const json = await res.json();
    if (!res.ok) throw new Error(typeof json.detail === 'string' ? json.detail : 'Falha ao montar o documento');
    return json;
  }, [qs]);

  const abrirPrevia = async (t) => {
    setBusyId(t.id);
    try { setPreview({ template: t, ...(await carregar(t)) }); }
    catch (e) { toast.error(e.message); } finally { setBusyId(''); }
  };

  const imprimir = async (t) => {
    setBusyId(t.id);
    try {
      const data = await carregar(t);
      if (data.missing_required?.length) setPreview({ template: t, ...data });
      printHtml(data.html);
    } catch (e) { toast.error(e.message); } finally { setBusyId(''); }
  };

  const baixarPdf = async (t) => {
    setBusyId(t.id);
    try {
      const res = await fetch(`${API_URL}/api/dna-sign/print/pdf?${qs(t.id)}`, { headers: authHeaders() });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(typeof j.detail === 'string' ? j.detail : 'Falha ao gerar o PDF');
      }
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement('a');
      a.href = url; a.download = `${t.name} - ${student.name || 'aluno'}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 20000);
      toast.success('PDF gerado (uso interno · sem envio para assinatura)');
    } catch (e) { toast.error(e.message); } finally { setBusyId(''); }
  };

  const contratos = catalog?.contratos || [];
  const cats = [{ key: 'todos', label: 'Todos' }, ...(catalog?.categories || [])];

  const faltantes = preview?.missing_required || [];
  const opcionais = (preview?.missing_variables || []).filter(v => !faltantes.includes(v));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl w-[97vw] h-[92vh] p-0 overflow-hidden flex flex-col gap-0 rounded-2xl"
                     data-testid="documentos-dnasign-dialog">
        <DialogHeader className="shrink-0 px-6 pt-5 pb-4 bg-gradient-to-br from-violet-800 via-violet-700 to-fuchsia-700 text-white">
          <DialogTitle className="flex items-center gap-2 text-lg font-bold">
            <FileText className="w-5 h-5" /> Documentos do aluno
          </DialogTitle>
          <p className="text-[11.5px] text-violet-100/90">
            {student?.name || '—'} · Matrícula {student?.registration_number || '—'} ·
            {' '}modelos do DNA Sign preenchidos automaticamente <strong>somente para impressão</strong>
          </p>
        </DialogHeader>

        {/* filtros */}
        <div className="shrink-0 border-b border-slate-200 bg-white px-5 py-3 space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input value={busca} onChange={e => setBusca(e.target.value)} className="pl-9"
                     placeholder="Buscar documento..." data-testid="docs-busca" />
            </div>
            {contratos.length > 0 && (
              <div className="flex items-center gap-1.5 flex-wrap" data-testid="docs-contexto">
                <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Contexto:</span>
                {contratos.map(c => (
                  <button key={c.id} type="button" onClick={() => setYear(String(c.academic_year))}
                          className={`text-[11px] font-bold px-2.5 py-1.5 rounded-lg border transition-colors ${
                            String(year) === String(c.academic_year)
                              ? 'bg-violet-600 text-white border-violet-600'
                              : 'bg-white text-slate-600 border-slate-200 hover:border-violet-300'}`}
                          data-testid={`docs-contexto-${c.academic_year}`}>
                    {c.academic_year} — contrato {c.contract_number}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            {cats.map(c => (
              <button key={c.key} type="button" onClick={() => setCat(c.key)}
                      className={`text-[11px] font-bold px-2.5 py-1 rounded-full border transition-colors ${
                        cat === c.key ? 'bg-slate-900 text-white border-slate-900'
                                      : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}
                      data-testid={`docs-cat-${c.key}`}>
                {c.label}
              </button>
            ))}
          </div>
        </div>

        {/* lista */}
        <div className="flex-1 overflow-y-auto bg-slate-50/70 px-5 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-20 text-slate-400 gap-2">
              <Loader2 className="w-5 h-5 animate-spin" /> Carregando modelos do DNA Sign...
            </div>
          ) : items.length === 0 ? (
            <p className="text-center py-20 text-sm text-slate-400" data-testid="docs-vazio">
              Nenhum modelo encontrado com esse filtro.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
              {items.map(t => (
                <div key={t.id} data-testid={`doc-card-${t.id}`}
                     className="rounded-xl border border-slate-200 bg-white p-4 flex flex-col gap-3 hover:border-violet-300 hover:shadow-sm transition-all">
                  <div className="flex items-start gap-2.5">
                    <div className="w-9 h-9 rounded-lg bg-violet-50 text-violet-600 flex items-center justify-center shrink-0 ring-1 ring-violet-100">
                      <FileText className="w-4.5 h-4.5" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-slate-900 leading-tight break-words">{t.name}</p>
                      <p className="text-[11px] text-slate-500 mt-0.5">
                        {t.category_label} · v{t.version || 1}
                        {t.status ? ` · ${t.status === 'draft' ? 'rascunho' : t.status}` : ''}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 mt-auto">
                    <Button size="sm" variant="outline" className="flex-1 h-8 text-[11px] font-bold"
                            disabled={busyId === t.id} onClick={() => abrirPrevia(t)}
                            data-testid={`doc-preview-${t.id}`}>
                      {busyId === t.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Eye className="w-3.5 h-3.5 mr-1" />Prévia</>}
                    </Button>
                    <Button size="sm" className="flex-1 h-8 text-[11px] font-bold bg-violet-600 hover:bg-violet-700"
                            disabled={busyId === t.id} onClick={() => imprimir(t)}
                            data-testid={`doc-print-${t.id}`}>
                      <Printer className="w-3.5 h-3.5 mr-1" />Imprimir
                    </Button>
                    <Button size="sm" variant="outline" className="h-8 px-2"
                            disabled={busyId === t.id} onClick={() => baixarPdf(t)}
                            title="Baixar PDF" data-testid={`doc-pdf-${t.id}`}>
                      <Download className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* prévia */}
        {preview && (
          <div className="absolute inset-0 z-20 bg-white flex flex-col" data-testid="doc-preview-panel">
            <div className="shrink-0 flex items-center gap-2 border-b border-slate-200 px-5 py-3 flex-wrap">
              <div className="flex-1 min-w-[200px]">
                <p className="text-sm font-bold text-slate-900">{preview.template?.name}</p>
                <p className="text-[11px] text-slate-500">
                  Ano {preview.ano_resolvido || '—'}
                  {preview.turma_resolvida ? ` · ${preview.turma_resolvida}` : ''}
                  {preview.contrato_resolvido ? ` · contrato ${preview.contrato_resolvido}` : ''}
                  {' '}· somente impressão
                </p>
              </div>
              <Button size="sm" className="bg-violet-600 hover:bg-violet-700 h-8 text-[11px] font-bold"
                      onClick={() => printHtml(preview.html)} data-testid="doc-preview-print">
                <Printer className="w-3.5 h-3.5 mr-1" /> Imprimir
              </Button>
              <Button size="sm" variant="outline" className="h-8 text-[11px] font-bold"
                      onClick={() => baixarPdf(preview.template)} data-testid="doc-preview-pdf">
                <Download className="w-3.5 h-3.5 mr-1" /> PDF
              </Button>
              <Button size="sm" variant="ghost" className="h-8" onClick={() => setPreview(null)}
                      data-testid="doc-preview-close">
                <X className="w-4 h-4" />
              </Button>
            </div>
            {faltantes.length > 0 && (
              <div className="shrink-0 bg-amber-50 border-b border-amber-200 px-5 py-3" data-testid="doc-missing-alert">
                <p className="text-[12.5px] font-bold text-amber-800 flex items-center gap-1.5">
                  <AlertTriangle className="w-4 h-4" /> Não foi possível preencher completamente este documento.
                </p>
                <div className="flex items-center gap-2 flex-wrap mt-2">
                  {faltantes.map(v => {
                    const [label, block] = varInfo(v);
                    return (
                      <button key={v} type="button"
                              onClick={() => { onOpenChange(false); onGoToField?.(block); }}
                              className="text-[11px] font-semibold bg-white border border-amber-300 text-amber-800 rounded-lg px-2.5 py-1 hover:bg-amber-100 inline-flex items-center gap-1"
                              data-testid={`doc-missing-${v}`}>
                        {label} <ArrowRight className="w-3 h-3" /> ir para campo
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {faltantes.length === 0 && opcionais.length > 0 && (
              <div className="shrink-0 bg-slate-50 border-b border-slate-200 px-5 py-2.5" data-testid="doc-optional-alert">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[11.5px] font-semibold text-slate-600">
                    Campos sem informação (não bloqueiam a impressão):
                  </span>
                  {opcionais.map(v => {
                    const [label, block] = varInfo(v);
                    return (
                      <button key={v} type="button"
                              onClick={() => { onOpenChange(false); onGoToField?.(block); }}
                              className="text-[11px] font-semibold bg-white border border-slate-300 text-slate-700 rounded-lg px-2.5 py-1 hover:bg-slate-100 inline-flex items-center gap-1"
                              data-testid={`doc-optional-${v}`}>
                        {label} <ArrowRight className="w-3 h-3" /> ir para campo
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <iframe title="Prévia do documento" srcDoc={preview.html}
                    className="flex-1 w-full bg-slate-100" data-testid="doc-preview-frame" />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DocumentosDnaSignDialog;
